import { forwardRef } from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '../../lib/cn.js'

/**
 * A single-line control. Every field in the product is this or the textarea.
 *
 * `invalid` is a prop rather than a class a caller adds, because an invalid field
 * has to do three things together — the ring, the `aria-invalid`, and the message
 * underneath — and a caller who styles it by hand gets the first one only.
 */
const input = cva(
  [
    'w-full min-w-0 box-border rounded-md border bg-surface text-ink',
    'placeholder:text-ink-muted',
    'outline-none transition-colors',
    'focus-visible:border-coral-fill focus-visible:ring-2 focus-visible:ring-coral-fill/25',
    'disabled:bg-sunk disabled:text-ink-muted disabled:cursor-not-allowed',
  ],
  {
    variants: {
      /* 40px, matching `Button` — not `.fawn-input`'s 48px. See `Button` for
         why the kit's control heights are a phone's and not this desk's, and
         why 36 was too thin. */
      size: { sm: 'h-9 px-3 text-second', md: 'h-10 px-3 text-body', lg: 'h-11 px-3 text-body' },
      invalid: {
        true: 'border-error focus-visible:border-error focus-visible:ring-error/25',
        false: 'border-hairline-strong',
      },
    },
    defaultVariants: { size: 'md', invalid: false },
  },
)

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'>, VariantProps<typeof input> {
  /** Sits inside the control, before the text. A search glass, a currency sign. */
  leading?: React.ReactNode
  trailing?: React.ReactNode
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, size, invalid, leading, trailing, ...props },
  ref,
) {
  if (!leading && !trailing) {
    return (
      <input
        ref={ref}
        aria-invalid={invalid || undefined}
        className={cn(input({ size, invalid }), className)}
        {...props}
      />
    )
  }
  return (
    <div className="relative flex items-center">
      {leading && (
        <span className="pointer-events-none absolute left-3 flex text-ink-muted [&_svg]:size-4">
          {leading}
        </span>
      )}
      <input
        ref={ref}
        aria-invalid={invalid || undefined}
        className={cn(input({ size, invalid }), leading && 'pl-9', trailing && 'pr-9', className)}
        {...props}
      />
      {trailing && (
        <span className="absolute right-3 flex text-ink-muted [&_svg]:size-4">{trailing}</span>
      )}
    </div>
  )
})
